import { MouseEvent, PointerEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clamp } from '@util/number';
import { useDerivedState } from '@hooks/useDerivedState';

type RowsResizeProps = {
    initialHeights: (number | null)[];
    onHeightChange: (i: number, height: number) => void;
    onHeightReset?: (i: number) => void;
    position?: 'top' | 'bottom';
    minHeight?: number;
    maxHeight?: number;
};

export const useRowsResize = ({
    initialHeights,
    onHeightChange,
    onHeightReset,
    position = 'top',
    minHeight = 0,
    maxHeight = 1e5,
}: RowsResizeProps) => {
    const [heights, setHeights] = useDerivedState(
        initialHeights.map((h) => h ?? minHeight),
        [initialHeights]
    );
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

    // Use refs to avoid rebinding mouse handlers on every move
    const anchorRef = useRef<number>(0);
    const draggingIndexRef = useRef<number | null>(null);
    const heightsRef = useRef<number[]>(heights);

    draggingIndexRef.current = draggingIndex;
    heightsRef.current = heights;

    const resizeHandlers = useMemo(() => {
        return initialHeights.map((_, i) => {
            const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
                const { current: heights } = heightsRef;
                const height = heights[i];

                e.preventDefault();
                e.stopPropagation();

                // Track click anchor relative to 0
                const sign = position == 'bottom' ? -1 : 1;
                anchorRef.current = sign * e.clientY - height;
                setDraggingIndex((draggingIndexRef.current = i));

                (e.target as Element)?.setPointerCapture(e.pointerId);
            };

            const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
                const { current: anchor } = anchorRef;
                const { current: draggingIndex } = draggingIndexRef;
                const { current: heights } = heightsRef;

                if (draggingIndex == null) return;
                e.stopPropagation();

                const sign = position == 'bottom' ? -1 : 1;
                const height = clamp(Math.round(sign * e.clientY - anchor), minHeight, maxHeight);

                const hs = [...heights];
                hs[i] = height;

                setHeights((heightsRef.current = hs));
            };

            const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
                const { current: heights } = heightsRef;
                const height = heights[i];

                e.stopPropagation();
                if (height != initialHeights[i]) onHeightChange(i, height);
                setDraggingIndex((draggingIndexRef.current = null));

                (e.target as Element)?.releasePointerCapture(e.pointerId);
            };

            const onDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                onHeightReset?.(i);
            };

            return { onPointerDown, onPointerMove, onPointerUp, onDoubleClick };
        });
    }, [initialHeights, setHeights, onHeightChange, onHeightReset, minHeight, maxHeight, position]);

    useLayoutEffect(() => {
        if (draggingIndex == null) return;

        // Add workaround for chrome bug - macOS pointerUp while moving mouse does not fire when pointer capture is active
        // https://bugs.chromium.org/p/chromium/issues/detail?id=1459632&q=pointerup%20event&can=2
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        document.addEventListener('pointerup', resizeHandlers[draggingIndex].onPointerUp as any);
        return () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            document.removeEventListener('pointerup', resizeHandlers[draggingIndex].onPointerUp as any);
        };
    }, [draggingIndex, resizeHandlers]);

    return { heights, draggingIndex, resizeHandlers };
};
